import { useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../config/msalConfig';
import {
    fetchCompleteUserProfile,
    clearUserProfileCache,
    getInitials
} from '../services/graphService';
import { User } from '../types';

interface UseUserProfileReturn {
    profile: Partial<User> | null;
    loading: boolean;
    error: string | null;
    isAuthenticated: boolean;
    initials: string;
    refreshProfile: () => void;
    clearProfile: () => void;
}

/**
 * Hook to get enhanced user profile from Microsoft Graph
 */
export function useUserProfile(): UseUserProfileReturn {
    const { instance, accounts, inProgress } = useMsal();
    const [profile, setProfile] = useState<Partial<User> | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Fetch profile from Graph API
    const fetchProfile = useCallback(async () => {
        if (accounts.length === 0 || inProgress !== 'none') {
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Get access token for Graph API
            const response = await instance.acquireTokenSilent({
                scopes: loginRequest.scopes || [],
                account: accounts[0]
            });

            // Fetch complete profile with photo
            const username = accounts[0]?.username;
            const userProfile = await fetchCompleteUserProfile(response.accessToken, username);
            setProfile(userProfile);
        } catch (err: any) {
            console.error('Error fetching user profile:', err);
            setError(err.message || 'Unknown error');

            // Fallback to basic MSAL account info
            if (accounts.length > 0) {
                setProfile({
                    id: accounts[0].localAccountId,
                    displayName: accounts[0].name,
                    email: accounts[0].username,
                    photoUrl: undefined
                });
            }
        } finally {
            setLoading(false);
        }
    }, [instance, accounts, inProgress]);

    // Fetch profile on mount and when accounts change
    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);

    // Refresh profile manually (useful after updates)
    const refreshProfile = useCallback(() => {
        clearUserProfileCache();
        fetchProfile();
    }, [fetchProfile]);

    // Clear profile on logout
    const clearProfile = useCallback(() => {
        clearUserProfileCache();
        setProfile(null);
    }, []);

    // Computed values
    const initials = useMemo(() => {
        return getInitials(profile?.displayName);
    }, [profile?.displayName]);

    const isAuthenticated = accounts.length > 0;

    return {
        profile,
        loading,
        error,
        isAuthenticated,
        initials,
        refreshProfile,
        clearProfile
    };
}

export default useUserProfile;
